import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  Inject,
  Input,
  OnChanges,
  OnInit,
  PLATFORM_ID,
  Renderer2,
  signal,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PropertyListModel } from '../../models/property-list.model';
import { SearchBoxContentModel } from '../../models/search-box.model';
import { SearchResultModel } from '../../models/search-result.model';
import { SettingsModel } from '../../models/setting.model';
import { SettingsService } from '../../services/settings.service';
import { SearchboxComponent } from '../../parts/search-box/search-box.component';
import { InnerHtmlComponent } from '../../parts/inner-html/inner-html.component';
import { SearchFilterComponent } from '../../parts/search-filter/search-filter.component';
import { SearchPropertyListComponent } from '../../parts/search-property-list/search-property-list.component';
import { ApiImagePipe } from '../../pipes/image.pipe';
import { SearchMapComponent } from '../../parts/search-map/search-map.component';
import { SearchService } from '../../services/search.service';
import { SearchCriteriaModel } from '../../models/search-criteria.model';
import { AppService } from '../../services/app.service';
import { PropertyMapCluster } from '../../models/property-map-cluster.model';
import { MatButtonModule } from '@angular/material/button';
import { TranslationPipe } from '../../pipes/translation.pipe';
import { environment } from '../../../environments/environment';
import { RoutingService } from '../../services/routing.service';

@Component({
  selector: 'app-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss'],
  imports: [
    SearchboxComponent,
    InnerHtmlComponent,
    SearchFilterComponent,
    SearchPropertyListComponent,
    ApiImagePipe,
    SearchMapComponent,
    CommonModule,
    MatButtonModule,
    TranslationPipe,
  ],
  standalone: true,
})
export class SearchComponent implements OnChanges, OnInit {
  loadingGoogleMapScript = signal(true);
  @Input() showSearch = true;
  @Input() data: SearchResultModel = new SearchResultModel();
  @Input() properties: PropertyListModel[] = [];

  currentSearchBoxData = new SearchBoxContentModel();
  settings: SettingsModel = new SettingsModel();
  searchResult: SearchResultModel = new SearchResultModel();

  searchCriteria: SearchCriteriaModel = new SearchCriteriaModel();

  isMobile = true;

  loading = true;

  isBrowser = false;

  listProperties?: PropertyListModel[];
  mapProperties?: PropertyMapCluster[];

  mapFullWidth = false;

  showNumberOfResults = 0;

  constructor(
    @Inject(PLATFORM_ID) private platformId: string,
    private settingsService: SettingsService,
    private appService: AppService,
    public dialog: MatDialog,
    private searchService: SearchService,
    private routingService: RoutingService,
    private renderer: Renderer2,
  ) {
    effect(() => {
      this.searchCriteria = this.searchService.clientSearchCriteria();

      this.mapFullWidth = this.searchService.mapFullWidth();
      if (
        this.routingService.route().Type == 1 ||
        this.routingService.route().Type == 4 ||
        this.routingService.route().Type == 2
      ) {
        if (this.showSearch) {
          this.getData();
        }
      }
    });
  }

  getData(loadMore: boolean = false) {
    this.searchCriteria = this.searchService.clientSearchCriteria();

    this.searchCriteria.ZoomLevel = Math.round(this.searchCriteria.ZoomLevel);
    this.appService
      .postData('Hotels', JSON.stringify(this.searchCriteria))
      .then((x: string) => {
        if (loadMore) {
          this.searchResult.Properties.push(...JSON.parse(x).Properties);
        } else {
          this.searchResult = JSON.parse(x);

          this.searchService.mapProperties.set(
            this.searchResult.PropertyMapCluster,
          );
        }

        this.showNumberOfResults =
          this.searchCriteria.Page > 0
            ? this.searchCriteria.Page * this.searchCriteria.PageSize
            : this.searchCriteria.PageSize;
      });
  }

  loadMoreHotels() {
    this.searchCriteria.Page++;
    this.getData(true);
  }

  ngOnChanges(): void {
    this.settings = this.settingsService.settings();
    this.isMobile = this.settingsService.getMobileStatus();

    this.setSearchBoxContent();
  }

  ngOnInit(): void {
    if (!this.showSearch) return;

    if (isPlatformBrowser(this.platformId)) {
      this.isBrowser = true;
      this.loadGoogleMapsScript();
      this.listenForGoogleMapsLoaded();
    }
  }

  listenForGoogleMapsLoaded() {
    window.addEventListener('googleMapsLoaded', () => {
      this.loadingGoogleMapScript.set(false);
    });
  }

  loadGoogleMapsScript() {
    if (
      document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]')
    ) {
      this.loadingGoogleMapScript.set(false);
      return;
    }
    const script = this.renderer.createElement('script');
    script.src = environment.googleMapScriptTag;
    script.async = true;
    script.defer = true;

    this.renderer.appendChild(document.body, script);
  }

  setSearchBoxContent() {
    if (this.data && this.data.SearchBoxDisplayName && this.data.SearchBoxUrl) {
      this.currentSearchBoxData.displayName = this.data.SearchBoxDisplayName;
      this.currentSearchBoxData.url = this.data.SearchBoxUrl;
    } else if (this.data.KayakUrl && this.data.Name) {
      this.currentSearchBoxData.displayName = this.data.Name;
      this.currentSearchBoxData.url =
        encodeURIComponent(this.data.Name) + '-' + this.data.KayakUrl;
    } else {
      this.currentSearchBoxData = new SearchBoxContentModel();
    }
  }

  changeMapFullWidth() {
    this.mapFullWidth = !this.mapFullWidth;
    this.searchService.mapFullWidth.set(this.mapFullWidth);
  }
}
